import { useState, useEffect, useCallback } from 'react';
import { CreditCard, X, Copy, AlertTriangle } from 'lucide-react';
import {
  billingAPI,
  type Subscription,
  type BillingInvoice,
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useModalKeys } from '../hooks/useModalKeys';

/**
 * Painel de billing — assinatura + faturas + PIX. Tema dark slate.
 */

const STATUS_LABEL: Record<Subscription['status'], string> = {
  trialing: 'Em teste',
  active: 'Ativa',
  past_due: 'Em atraso',
  canceled: 'Cancelada',
  unpaid: 'Não paga',
  incomplete: 'Incompleta',
  incomplete_expired: 'Incompleta (expirada)',
};

const STATUS_COLOR: Record<Subscription['status'], string> = {
  trialing: 'bg-primary-soft/40 text-primary border border-primary-line/50',
  active: 'bg-success-soft/40 text-success border border-success-line/50',
  past_due: 'bg-warning-soft/40 text-warning border border-warning-line/50',
  canceled: 'bg-hover text-fg-2 border border-active',
  unpaid: 'bg-danger-soft/40 text-danger border border-danger-line/50',
  incomplete: 'bg-hover text-fg-2 border border-active',
  incomplete_expired: 'bg-hover text-muted border border-active',
};

const INVOICE_BADGE: Record<BillingInvoice['status'], string> = {
  draft: 'bg-hover text-fg-2 border border-active',
  open: 'bg-warning-soft/30 text-warning border border-warning-line/50',
  paid: 'bg-success-soft/30 text-success border border-success-line/50',
  uncollectible: 'bg-danger-soft/30 text-danger border border-danger-line/50',
  void: 'bg-hover text-muted line-through border border-active',
};

function formatBRL(cents: number): string {
  return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatDate(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('pt-BR');
}

function errorMsg(err: unknown): string {
  const e = err as { response?: { data?: { message?: string } }; message?: string };
  return e.response?.data?.message ?? e.message ?? 'Erro desconhecido';
}

export default function Billing() {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [invoices, setInvoices] = useState<BillingInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pixModal, setPixModal] = useState<{
    invoiceId: string;
    qrCode: string;
    expiresAt: string;
  } | null>(null);
  const [pixLoading, setPixLoading] = useState(false);

  // Modal PIX: Esc fecha (Fase 30)
  useModalKeys(() => setPixModal(null), undefined, !!pixModal);

  const isAdmin = user?.role === 'admin';

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [sub, invs] = await Promise.all([
        billingAPI.subscription(),
        billingAPI.invoices({ limit: 50 }),
      ]);
      setSubscription(sub);
      setInvoices(invs.data);
    } catch (err) {
      setError(errorMsg(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function generatePix(invoiceId: string) {
    setPixLoading(true);
    try {
      const r = await billingAPI.pix(invoiceId);
      setPixModal({
        invoiceId,
        qrCode: r.pixQrCode,
        expiresAt: r.expiresAt,
      });
    } catch (err) {
      alert(`Falha PIX: ${errorMsg(err)}`);
    } finally {
      setPixLoading(false);
    }
  }

  async function setupCard() {
    try {
      const r = await billingAPI.setupIntent();
      alert(
        'SetupIntent criado.\n\n' +
          `clientSecret: ${r.clientSecret.slice(0, 40)}…\n\n` +
          'Próxima fase: integrar @stripe/react-stripe-js pra Elements UI.',
      );
    } catch (err) {
      alert(`Falha setup intent: ${errorMsg(err)}`);
    }
  }

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto">
      <header className="mb-6 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary-soft/40 text-primary flex items-center justify-center">
          <CreditCard size={20} />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-fg">Assinatura</h1>
          <p className="text-sm text-fg-2">Plano, cartão e faturas</p>
        </div>
      </header>

      {error && (
        <div className="mb-4 rounded-lg border border-danger-line/50 bg-danger-soft/30 px-4 py-3 text-sm text-danger">
          {error}
        </div>
      )}

      {/* Card de assinatura */}
      <section className="mb-6 rounded-2xl border border-line bg-surface p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-semibold text-fg">Assinatura atual</h2>
          {subscription && (
            <span
              className={`rounded-full px-3 py-1 text-xs font-medium uppercase ${STATUS_COLOR[subscription.status]}`}
            >
              {STATUS_LABEL[subscription.status]}
            </span>
          )}
        </div>

        {loading ? (
          <p className="text-sm text-muted">Carregando…</p>
        ) : !subscription ? (
          <div className="text-center py-8">
            <p className="text-sm text-fg-2 mb-3">
              Nenhuma assinatura ativa neste deploy.
            </p>
            <p className="text-xs text-muted">
              Entre em contato com a Farisx pra ativar seu plano e habilitar módulos.
            </p>
          </div>
        ) : (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-xs uppercase tracking-wider text-muted mb-1">
                  Cliente
                </div>
                <div className="text-fg">{subscription.clientName}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-muted mb-1">
                  Mensalidade
                </div>
                <div className="text-fg font-medium">
                  {subscription.monthlyAmountCents != null
                    ? formatBRL(subscription.monthlyAmountCents)
                    : '—'}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-muted mb-1">
                  Próximo ciclo
                </div>
                <div className="text-fg">{formatDate(subscription.currentPeriodEnd)}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-muted mb-1">
                  Módulos
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {subscription.items.length === 0 ? (
                    <span className="text-muted">—</span>
                  ) : (
                    subscription.items.map((i) => (
                      <span
                        key={i.moduleId}
                        className="inline-block px-2 py-0.5 rounded-full bg-primary-soft/40 text-primary border border-primary-line/50 text-xs"
                      >
                        {i.moduleId}
                      </span>
                    ))
                  )}
                </div>
              </div>
            </div>

            {subscription.cancelAtPeriodEnd && (
              <div className="flex items-start gap-2 mt-4 rounded-lg border border-warning-line/50 bg-warning-soft/30 px-3 py-2 text-xs text-warning">
                <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                <span>Cancelamento agendado pro fim do ciclo atual</span>
              </div>
            )}

            {isAdmin && (
              <button
                onClick={setupCard}
                className="mt-4 rounded-lg bg-primary-solid hover:bg-primary-solid-hover px-4 py-2 text-sm font-medium text-white transition"
              >
                Atualizar cartão
              </button>
            )}
          </div>
        )}
      </section>

      {/* Lista de invoices */}
      <section className="rounded-2xl border border-line bg-surface overflow-hidden">
        <header className="border-b border-line p-4">
          <h2 className="font-semibold text-fg">Faturas</h2>
        </header>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-canvas border-b border-line">
              <tr className="text-left text-xs font-semibold uppercase tracking-wider text-muted">
                <th className="px-4 py-3">Número</th>
                <th className="px-4 py-3">Data</th>
                <th className="px-4 py-3">Vencimento</th>
                <th className="px-4 py-3">Valor</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-muted">
                    Carregando…
                  </td>
                </tr>
              ) : invoices.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-muted">
                    Sem faturas ainda.
                  </td>
                </tr>
              ) : (
                invoices.map((inv) => (
                  <tr key={inv.id} className="hover:bg-canvas/50 transition">
                    <td className="px-4 py-3 font-mono text-xs text-fg-2">
                      {inv.number ?? '—'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-fg-2">
                      {formatDate(inv.createdAt)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-fg-2">
                      {formatDate(inv.dueDate)}
                    </td>
                    <td className="px-4 py-3 font-medium text-fg">
                      {formatBRL(inv.amountDueCents)}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs ${INVOICE_BADGE[inv.status]}`}
                      >
                        {inv.status}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-xs">
                      {inv.hostedUrl && (
                        <a
                          href={inv.hostedUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="mr-3 text-primary hover:text-primary hover:underline"
                        >
                          Ver
                        </a>
                      )}
                      {inv.invoicePdfUrl && (
                        <a
                          href={inv.invoicePdfUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="mr-3 text-fg-2 hover:text-fg hover:underline"
                        >
                          PDF
                        </a>
                      )}
                      {isAdmin && inv.status === 'open' && (
                        <button
                          onClick={() => generatePix(inv.id)}
                          disabled={pixLoading}
                          className="text-success hover:text-success hover:underline disabled:opacity-50"
                        >
                          {pixLoading ? '…' : 'PIX'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Modal PIX */}
      {pixModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-md rounded-2xl bg-surface border border-line p-6">
            <header className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-bold text-fg">Pagar com PIX</h3>
              <button
                onClick={() => setPixModal(null)}
                className="text-muted hover:text-fg-2"
              >
                <X size={20} />
              </button>
            </header>

            <p className="mb-3 text-sm text-fg-2">
              Abra o app do seu banco, escaneie o QR ou cole o código abaixo.
            </p>

            <div className="mb-3 rounded-lg border border-active bg-canvas p-3">
              <code className="break-all text-xs text-fg-2">{pixModal.qrCode}</code>
            </div>

            <p className="text-xs text-muted">
              Expira em: {new Date(pixModal.expiresAt).toLocaleString('pt-BR')}
            </p>

            <button
              onClick={() => {
                navigator.clipboard.writeText(pixModal.qrCode);
                alert('Copiado!');
              }}
              className="mt-4 w-full flex items-center justify-center gap-2 rounded-lg bg-primary-solid hover:bg-primary-solid-hover px-4 py-2 text-sm font-medium text-white transition"
            >
              <Copy size={16} />
              Copiar código
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
